import {
  Box,
  Paper,
  useTheme,
  Skeleton,
  Menu,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";

import useStyles from "../styles";
import { Fragment, JSX, useEffect, useMemo, useState } from "react";

import { Interval } from "../../../functions/formatData/types";
import TemplateGraphic from "./templateGraphic";
import MyHeader from "../myHeader";
import OptionsControls from "./settingChartOptions";
import {
  ContentCopy,
  DeleteOutline,
  Edit,
  MoreVert,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useFilters } from "../../../context/filterContext";
import api from "../../../services/api";

interface TemplateCardProps {
  index: number;
  card: DashCards;
  isCardLoading?: any;
  firstLoading?: boolean;
  editMode: boolean;
  isEditing: boolean;
  handleDelete: (id: string) => void;
  duplicateCard?: (card: DashCardsGraphics) => void;
  updateCard?: (updatedCard: DashCardsGraphics) => void;
  filtersInitialized: boolean;
  paginaId?: string;
  refreshKey?: number;
}

type Orientation = "horizontal" | "vertical";
type OrientationObj = Record<string, Orientation>;

function TemplateCard(props: TemplateCardProps): JSX.Element {
  const {
    index,
    card,
    firstLoading,
    editMode,
    isEditing,
    handleDelete,
    duplicateCard,
    updateCard,
    paginaId,
    filtersInitialized,
    refreshKey,
    isCardLoading,
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [cardData, setCardData] = useState<DashCardsGraphics | null>();
  const [localLoading, setLocalLoading] = useState(false);
  const theme = useTheme();
  const styles = useStyles(theme);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const { filtersConfig, filters: contextFilters } = useFilters();

  const hasPropData = useMemo(
    () =>
      card.graphics?.some(
        (g: any) => g.config?.data && g.config?.data.length > 0,
      ),
    [card.graphics],
  );

  const showSkeleton =
    firstLoading || (localLoading && !cardData && !hasPropData);

  function parseOptions(graphic: DashGraphicsCards) {
    return graphic.map((item: any) => ({
      label: item.label,
      value: item.value[0],
    }));
  }
  function handleOrientation(orientation: Orientation | OrientationObj) {
    if (typeof orientation === "object") {
      const result: Record<string, "row" | "column"> = {};
      for (const key in orientation) {
        if (orientation.hasOwnProperty(key))
          result[key] = orientation[key] === "horizontal" ? "row" : "column";
      }
      return result;
    }
    return orientation === "horizontal" ? "row" : "column";
  }

  let hasKeyOption = card?.graphics?.find(
    (graphic: DashGraphicsCards) => !!graphic.config?.keysOptions,
  );

  if (hasKeyOption) {
    hasKeyOption = parseOptions(hasKeyOption.config.keysOptions);
  }
  const granularityData = JSON.parse(
    localStorage.getItem("granularity") || "{}",
  );
  const viewModeData = JSON.parse(localStorage.getItem("viewMode") || "{}");

  const repeatByData = JSON.parse(localStorage.getItem("repeatBy") || "{}");
  const hasGranularity =
    card?.graphics?.some(
      (graphic: DashGraphicsCards) => graphic.config?.groupBy === "date",
    ) || false;
  const filterFirstInterval = card?.graphics?.find(
    (graphic: DashGraphicsCards) => !!graphic.config.interval,
  );
  const filterFirstViewMode = card?.graphics?.find(
    (graphic: DashGraphicsCards) => !!graphic.config.viewMode,
  );
  const filterFirstRepeatBy = card?.graphics?.find(
    (graphic: DashGraphicsCards) => !!graphic.config.repeatBy,
  );
  const findGraphic = card?.graphics?.find(
    (graphic: DashGraphicsCards) => graphic,
  );

  const [intervalStateCard, setIntervalStateCard] = useState<
    Interval | undefined
  >(
    granularityData[findGraphic?.id]
      ? granularityData[findGraphic.id]
      : filterFirstInterval?.config.interval
        ? filterFirstInterval?.config.interval
        : "month",
  );

  const [viewModeStateCard, setViewModeStateCard] = useState<
    string | undefined
  >(
    viewModeData[findGraphic.id]
      ? viewModeData[findGraphic.id]
      : filterFirstViewMode?.config.viewMode
        ? filterFirstViewMode?.config.viewMode
        : "linear",
  );

  const [repeatByStateCard, setRepeatByStateCard] = useState<string | null>(
    repeatByData[findGraphic.id]
      ? repeatByData[findGraphic.id]
      : filterFirstRepeatBy?.config.repeatBy
        ? filterFirstRepeatBy?.config.repeatBy
        : null,
  );

  const [keysOptions, setKeysOptions] = useState<DashGraphicsCards | undefined>(
    hasKeyOption ? hasKeyOption[0].value : undefined,
  );

  const graphicsMemo = useMemo(() => {
    const newGraphics = card?.graphics?.map((graphic: DashGraphicsCards) => {
      if (
        (graphic.type === "bar" ||
          graphic.type === "lineBar" ||
          graphic.type === "line" ||
          graphic.type === "repeat") &&
        !card.split
      ) {
        if (!card.split) {
          granularityData[graphic.id] = intervalStateCard;
          localStorage.setItem("granularity", JSON.stringify(granularityData));
          viewModeData[graphic.id] = viewModeStateCard;
          localStorage.setItem("viewMode", JSON.stringify(viewModeData));
          repeatByData[graphic.id] = repeatByStateCard;
          localStorage.setItem("repeatBy", JSON.stringify(repeatByData));
        }

        return {
          ...graphic,
          config: {
            ...graphic.config,
            ...(graphic.type !== "repeat"
              ? {
                  interval: intervalStateCard ? intervalStateCard : undefined,
                  viewMode: viewModeStateCard,
                  keys: keysOptions ? [keysOptions] : graphic.config.keys,
                }
              : { repeatBy: repeatByStateCard }),
            repeatBy: repeatByStateCard,
          },
        };
      }

      return graphic;
    });
    return newGraphics;
  }, [
    intervalStateCard,
    viewModeStateCard,
    repeatByStateCard,
    keysOptions,
    card,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCardData = async () => {
      if (!card.id || !paginaId || !contextFilters || !filtersInitialized) {
        return;
      }

      try {
        setLocalLoading(true);
        const response = await api.post(
          "/cards/data/find",
          {
            id: card.id,
            pageId: paginaId,
            filters: contextFilters,
          },
          { signal: controller.signal },
        );

        if (response.status === 200) {
          setCardData(response.data);
        }
      } catch (error: any) {
        if (error.name !== "CanceledError") {
          console.error(`Erro no card ${card.id}:`, error);
        }
      } finally {
        setLocalLoading(false);
      }
    };

    fetchCardData();

    return () => {
      controller.abort();
    };
  }, [contextFilters, card.id, paginaId, filtersInitialized, refreshKey]);

  const finalGraphics = useMemo(() => {
    const baseGraphics = graphicsMemo;

    return baseGraphics.map((g: DashGraphicsCards) => {
      const remoteG = cardData?.graphics?.find((rg) => rg.id === g.id);

      if (remoteG) {
        return {
          ...g,
          config: { ...g.config, data: remoteG.data, metrics: remoteG.metrics },
          error: remoteG.error || g.error,
        };
      }

      const propG = card.graphics?.find((pg: any) => pg.id === g.id);
      if (propG?.config?.data) {
        return {
          ...g,
          config: {
            ...g.config,
            data: propG.config.data,
            metrics: propG.config.metrics,
          },
        };
      }

      return g;
    });
  }, [graphicsMemo, cardData, card.graphics]);

  return (
    <Box key={"grid" + index} sx={{ width: "100%", height: "100%" }}>
      {showSkeleton ? (
        <>
          {editMode && (
            <Box
              sx={{
                position: "absolute",
                right: 6,
                top: 6,
                zIndex: 99,
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
                size="small"
                className="cancelSelectorName"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={typeof card.height === "string" ? card?.height : "100%"}
            animation="wave"
            sx={{
              opacity: theme.palette.mode === "dark" ? 0.4 : 0.8,
              minHeight: "132px",
            }}
          />{" "}
        </>
      ) : (
        <>
          <Paper
            sx={styles.paper(
              card?.height,
              card.split,
              card.background,
              card.noPadding,
            )}
          >
            {!card.split && (
              <MyHeader
                title={card.title}
                subtitle={card.subtitle}
                noPadding={card.noPadding}
                description={card.description}
              >
                {(!editMode || isEditing) && (
                  <OptionsControls
                    hasKeyOption={hasKeyOption}
                    hasGranularity={hasGranularity}
                    keysOptions={keysOptions}
                    setKeysOptions={setKeysOptions}
                    intervalStateCard={intervalStateCard}
                    setIntervalStateCard={(value: string) =>
                      setIntervalStateCard(value as Interval)
                    }
                    viewModeStateCard={viewModeStateCard}
                    setViewModeStateCard={(value: string) =>
                      setViewModeStateCard(value as string)
                    }
                    repeatByStateCard={repeatByStateCard}
                    setRepeatByStateCard={(value: string) =>
                      setRepeatByStateCard(value as string)
                    }
                    graphic={findGraphic}
                  />
                )}
              </MyHeader>
            )}
            <Box
              key={"box" + index}
              sx={styles.boxGraphic(
                handleOrientation(card.orientation),
                !!card?.separator,
              )}
            >
              {finalGraphics?.map(
                (graphic: DashGraphicsCards, index: number) => (
                  <>
                    <Fragment key={index}>
                      <TemplateGraphic
                        key={index}
                        graphic={graphic}
                        index={index}
                        card={card}
                        loading={localLoading || isCardLoading}
                        isEditing={isEditing}
                        setAnchorEl={setAnchorEl}
                        editMode={editMode}
                        updateCard={updateCard}
                        deleteCard={handleDelete}
                        duplicateCard={duplicateCard}
                        selectedFilter={filtersConfig?.find(
                          (f) => f.variable === repeatByStateCard,
                        )}
                        paginaId={paginaId}
                        firstLoading={Boolean(firstLoading)}
                        filtersInitialized={filtersInitialized}
                      />
                    </Fragment>
                  </>
                ),
              )}
            </Box>
          </Paper>
        </>
      )}
      {anchorEl && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          key={card.id}
        >
          <MenuList>
            <MenuItem
              color="text.secondary"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                setAnchorEl(null);
                navigate(`${window.location.pathname}/${card.id}`);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Editar gráfico</ListItemText>
            </MenuItem>
            <MenuItem
              color="text.secondary"
              onClick={() => {
                setAnchorEl(null);
                handleDelete(card.id);
              }}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Remover gráfico</ListItemText>
            </MenuItem>
            <MenuItem
              color="text.secondary"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                setAnchorEl(null);
                const cardWithCurrentData = {
                  ...card,
                  graphics: finalGraphics.map((fg: DashGraphicsCards) => ({
                    ...fg,
                    config: { ...fg.config },
                  })),
                };

                duplicateCard?.(cardWithCurrentData as DashCardsGraphics);
              }}
            >
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicar gráfico</ListItemText>
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </Box>
  );
}
export default TemplateCard;
